import * as Sentry from "@sentry/react";
import { decrypt, encrypt } from "./others";
import { API } from "../../Services/api-service";
import setAuthCookies from "../../SharedComponents/setAuthCookies";
import { clearLoggedOutFlag } from "./cookieUtils";
import { decodeToken } from "./decodeToken";

/**
 * Quick boolean check: returns true if any (potentially partial) session
 * object exists in localStorage. Does NOT verify token presence.
 * Use `authenticate()` for a stricter check before making API calls.
 *
 * @returns {boolean}
 */
export const auth = () => !!decrypt(localStorage.sandbox_user);

/**
 * Strict session check used by route guards.
 * Returns true only when BOTH `Authorization` (access token) and
 * `refresh-token` are present in the decrypted session object.
 *
 * @returns {boolean}
 */
export const authenticate = () => {
  if (localStorage.sandbox_user) {
    const ls = decrypt(localStorage.sandbox_user);
    if (!ls.Authorization) return false;
    if (!ls["refresh-token"]) return false;
    return true;
  } else {
    return false;
  }
};

/**
 * Returns the role stored in the session (e.g. "student", "parent").
 * Role is written by `saveSession` from the JWT claims or API response.
 *
 * @returns {string} role value
 */
export const getRole = () => {
  const role = decrypt(localStorage.sandbox_user);
  return role.role;
};

/**
 * Returns whether the current user is a paid user.
 * Sourced from the `isPaid` field saved in the session during `saveSession`.
 *
 * @returns {boolean}
 */
export const checkIfPaidUser = () => {
  const user = decrypt(localStorage.sandbox_user);
  return user?.isPaid === true;
};

/**
 * Patches the `isPaid` field in all localStorage session keys without
 * touching other session data. Dispatches `sandbox_user_update` so any
 * listener (e.g. ProtectedRoute) can re-render with the new value.
 *
 * @param {boolean} isPaid
 */
export const updateIsPaidInSession = (isPaid) => {
  if (!localStorage.sandbox_user) return;
  const current = decrypt(localStorage.sandbox_user);
  if (!current) return;
  const updated = { ...current, isPaid };
  localStorage.setItem("sandbox_user", encrypt(updated));
  localStorage.setItem("sandbox_user_data", encrypt(updated));
  localStorage.setItem("user", encrypt(updated));
  window.dispatchEvent(new Event("sandbox_user_update"));
};

/**
 * Patches the `phone` field in all localStorage session keys without
 * touching other session data. Guests commonly reach Sandbox via the
 * cross-app cookie handoff (`bootstrapSessionFromCookie`), whose JWT carries
 * no phone claim, so `phone` is often missing from the session until a
 * profile API backfills it (see GuestUserDashboard).
 *
 * @param {string} phone
 */
export const updatePhoneInSession = (phone) => {
  if (!phone || !localStorage.sandbox_user) return;
  const current = decrypt(localStorage.sandbox_user);
  if (!current || current.phone === phone) return;
  const updated = { ...current, phone };
  localStorage.setItem("sandbox_user", encrypt(updated));
  localStorage.setItem("sandbox_user_data", encrypt(updated));
  localStorage.setItem("user", encrypt(updated));
  window.dispatchEvent(new Event("sandbox_user_update"));
};

/**
 * Returns the `user_id` stored in the session.
 * Sourced from the JWT `userId` claim or the `userId` field in the API response.
 *
 * @returns {string} user ID
 */
export const getUserId = () => {
  const user = decrypt(localStorage.sandbox_user);
  return user.user_id;
};
/**
 * Returns the subscription object from `sandbox_user_data` localStorage key.
 * This key is separate from the session key and holds richer profile data.
 *
 * @returns {Object|null} Subscription data, or null if unavailable
 */
export const getUserSubscriptionData = () => {
  if (!localStorage.sandbox_user_data) return null;
  const sandbox_user_data = decrypt(localStorage.sandbox_user_data);
  if (!sandbox_user_data.subscription) return null;
  return sandbox_user_data.subscription;
};

// Lives in its own module so the API client's helpers can use it without a
// circular import; re-exported here for existing callers.
export { decodeToken };

/**
 * Persists auth tokens and user profile to localStorage and syncs the
 * in-memory Axios instance so subsequent requests are immediately authenticated.
 *
 * Steps performed:
 * 1. Decodes `accessToken` JWT to extract fallback values (role, userId, email)
 * 2. Builds a `sessionData` object merging JWT claims with `userData`
 * 3. Encrypts and saves to `localStorage.sandbox_user`
 * 4. Calls `API.updateTokens` so the Axios instance uses the new tokens
 * 5. Dispatches a `sandbox_user_update` window event so other listeners
 *    (e.g. the Axios interceptor in api-service.js) can refresh in-memory tokens
 *
 * @param {Object} params
 * @param {string} params.accessToken   - JWT access token from the auth API
 * @param {string} params.refreshToken  - Refresh token from the auth API
 * @param {Object} [params.userData={}] - Optional extra user fields from the API response
 *   (role, userId, email, phone, userName, profilePicUrl, isPaid)
 */
export const saveSession = ({ accessToken, refreshToken, userData = {} }) => {
  const decoded = decodeToken(accessToken);

  const sessionData = {
    ACCESS_TOKEN: accessToken,
    REFRESH_TOKEN: refreshToken,
    Authorization: accessToken,
    "refresh-token": refreshToken,
    role: userData.role || decoded.role || "student",
    user_id: userData.userId || decoded.userId || "",
    email: userData.email || decoded.studentEmail || decoded.email || "",
    studentEmail: userData.email || decoded.studentEmail || decoded.email || "",
    phone: userData.phone || decoded.phone || "",
    userName: userData.userName || "",
    studentName: userData.studentName || decoded.studentName || "",
    profilePicUrl: userData.profilePicUrl || "",
    isPaid: userData.isPaid ?? false,
    studentId: userData.userId || decoded.userId || "",
    userId: userData.userId || decoded.userId || "",
    grade: userData.grade || decoded.grade || "",
  };
  localStorage.setItem("sandbox_user", encrypt(sessionData));
  localStorage.setItem("studentId", sessionData.studentId); // Legacy key for MFE compatibility
  localStorage.setItem("sandbox_user_data", encrypt(sessionData)); // For richer profile data access (e.g. subscription) without decryption issues
  localStorage.setItem("user", encrypt(sessionData)); // For MFE mode where portal reads from `user`
  API.updateTokens({ ACCESS_TOKEN: accessToken, REFRESH_TOKEN: refreshToken });
  // Identify the user on Sentry so auth-flow events stop being anonymous.
  // Id only — no email/name, keeping PII out of the FE project.
  if (sessionData.userId) {
    Sentry.setUser({ id: sessionData.userId });
  }
  window.dispatchEvent(new Event("sandbox_user_update"));

  // A fresh session write revives auth — clear any cross-app logout signal so an
  // immediately-following bootstrap/focus check doesn't tear this session down.
  try {
    clearLoggedOutFlag();
  } catch (_) {
    /* no-op */
  }

  // Shared cross-app cookies on .codeyoung.com — the auto-login + cross-tab-sync
  // transport. STUDENT-ONLY: only a student session participates in cross-app
  // auto-login (admins & mentors stay app-local), so we don't publish their
  // tokens to the portal. They still work fully inside Sandbox via localStorage.
  // Impersonated-student sessions (staff viewing as a student; 15m, never
  // refreshable) are also excluded — publishing them would plant soon-dead
  // tokens as the browser-wide student session.
  try {
    if (sessionData.role === "student" && !decoded.impersonatedBy) {
      setAuthCookies(
        { ACCESS_TOKEN: accessToken, REFRESH_TOKEN: refreshToken },
        1,
      );
    }
  } catch (_) {
    // Cookie failures must never block auth completion.
  }

  // NewRelic browser-agent user identification — guarded so it no-ops when
  // the agent script failed to load (REACT_APP_ENABLE_NEWRELIC !== "1").
  try {
    if (window.newrelic) {
      if (sessionData.userId)
        window.newrelic.setCustomAttribute("userId", sessionData.userId);
      if (sessionData.email)
        window.newrelic.setCustomAttribute("email", sessionData.email);
      if (sessionData.role)
        window.newrelic.setCustomAttribute("role", sessionData.role);
    }
  } catch (_) {
    /* no-op */
  }
};
